import React, {useEffect, useState} from "react";
import Diagram, {
    Nodes,
    AutoLayout,
    Toolbox,
    Group,
    Edges,
    CustomShape,
} from "devextreme-react/diagram";
import "./DocumentsHierarchyPage.css";
import {toast} from "react-toastify";
import {v4 as uuidv4} from "uuid";
import {LoadPanel} from "devextreme-react";
import {useTranslation} from "react-i18next";
import {useAppDispatch, useAppSelector} from "../../../redux/hooks/hooks";
import {AppEntitiesDocuments, AppServices} from "../../../config/entities";
import {ReferenceTypes} from "../../../enums/ReferenceTypes";
import {addTab, ITab} from "../../../redux/slices/newTabSlice";
import {NewDocumentModalView} from "../../Shared/NewDocumentModalView";

interface HierarchyProps {
    documentId: string;
    handleTabClose: (tab: ITab) => void;
    handleCloseDetailView: (result: boolean) => void;
}

interface IProps {
    id?: string;
    documentPresentationName?: string;
    typeDocument?: string;
    referenceType?: number;
    postStatus?: number;
    childrens: IProps[];
}

const DocumentsHierarchyPage = ({
                                    documentId,
                                    handleTabClose,
                                    handleCloseDetailView,
                                }: HierarchyProps) => {
    const {t} = useTranslation();
    const [clonedObject, setClonedObject] = useState<any>(null);
    const activeTabId = useAppSelector((state: any) => state.newTab.activeTab);
    const tabs = useAppSelector((state) => state.newTab.tabs);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const fetchData = async () => {
        try {
            setIsLoading(true);
            const response = await AppServices.Axios.get(
                `/DocumentTrees/${documentId}`
            );
            const rawData = response.data;
            const newHierarchyData = getHierarchyData(rawData);
            setHierarchyData(newHierarchyData[0]);
            setHierarchyEdges(newHierarchyData[1]);
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [documentId]);

    const getHierarchyData = (document: IProps, parent?: IProps) => {
        let nodes = [
            {
                title: document.documentPresentationName,
                id: document.id,
                referenceType: document.referenceType,
            },
        ];
        let edges: any[] = [];

        if (parent) {
            edges.push({
                from: parent.id,
                to: document.id,
            });
        }

        for (const child of document.childrens) {
            const [childNodes, childEdges] = getHierarchyData(child, document);
            nodes = nodes.concat(childNodes);
            edges = edges.concat(childEdges);
        }

        return [nodes, edges];
    };

    const [hierarchyData, setHierarchyData] = useState<any[]>([]);
    const [hierarchyEdges, setHierarchyEdges] = useState<any[]>([]);

    const openDocumentInNewTab = async (
        documentId: string,
        documentReferenceType: ReferenceTypes
    ) => {
        try {
            AppEntitiesDocuments.forEach((ent) => {
                if (ent.apiEnum === documentReferenceType) {
                    console.log("Found entity:", ent);
                }
            });
            const matchedEntities = AppEntitiesDocuments.filter(
                (ent) => ent.apiEnum === documentReferenceType
            );
            console.log(matchedEntities);
            const entity = AppEntitiesDocuments.find(
                (ent) => Number(ent.apiEnum) === Number(documentReferenceType)
            );
            const dataService = entity?.getDataService();
            const item = await dataService.Get(documentId);
            const id = uuidv4();
            const newTab: ITab = {
                id,
                title: item.title,
                entity,
                side: "left",
                isModified: false,
                component: (
                    <NewDocumentModalView
                        entity={entity}
                        onClose={(event) => {
                            handleTabClose(newTab);
                            handleCloseDetailView(true);
                        }}
                        id={documentId}
                        clonedObject={clonedObject}
                    />
                ),
            };
            dispatch(addTab(newTab));
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    const handleItemClick = async (e: any) => {
        if (e.item.itemType === "shape") {
            const documentId = e.item.dataItem.id;
            const documentReferenceType = e.item.dataItem.referenceType;
            await openDocumentInNewTab(documentId, documentReferenceType);
        }
    };

    return (
        <>
            <Diagram
                id="diagram"
                readOnly={true}
                showGrid={false}
                simpleView={true}
                onItemDblClick={handleItemClick}
            >
                <Nodes dataSource={hierarchyData} keyExpr="id" textExpr="title">
                    <AutoLayout orientation="vertical" type="tree"/>
                    <CustomShape
                        category="documents"
                        type="documentShape"
                        title="Document"
                        baseType="rectangle"
                        defaultWidth={120}
                        defaultHeight={60}
                        toolboxWidthToHeightRatio={2}
                        allowEditText={false}
                        allowResize={false}
                    />
                </Nodes>
                <Edges
                    dataSource={hierarchyEdges}
                    keyExpr="from"
                    fromExpr="from"
                    toExpr="to"
                />
                <Toolbox visibility="disabled">
                    <Group category="general" title="General"/>
                </Toolbox>
            </Diagram>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.1)"
                visible={isLoading}
                showIndicator={true}
                showPane={true}
                shading={true}
                message={t("Loading")}
            />
        </>
    );
};

export default DocumentsHierarchyPage;
